import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import Overview from './Overview';
import WalletNew from './WalletNew';
import WalletLayoutPage from './WalletLayoutPage';
import Exchange from './Exchange';
import TransactionsPage from './TransactionsPage';
import HorizontalNavBar from './HorizontalNavBar';

// Global notification state for the application
export const NotificationContext = React.createContext({
  notification: { message: '', type: '' },
  showNotification: () => {}
});

const Dashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '' });

  // Show notification function
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification({ message: '', type: '' });
    }, 5000);
  };

  // Handle hash change for navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        setActiveTab(hash);
      }
    };

    // Set initial tab based on URL hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Handle tab click
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    window.location.hash = tab;
  };

  // Handle tab content and loading state
  useEffect(() => {
    // Set loading state when tab changes
    setIsLoading(true);

    // Clear loading state after a short delay
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);

    // Clean up timer
    return () => clearTimeout(timer);
  }, [activeTab]);

  // Render the active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <Overview />;
      case 'wallet':
        return <WalletLayoutPage />;
      case 'exchange':
        return <Exchange />;
      case 'transactions':
        return <TransactionsPage />;
      case 'settings':
        return <div className="placeholder-content">Settings content coming soon</div>;
      default:
        return <Overview />;
    }
  };

  return (
    <NotificationContext.Provider value={{ notification, showNotification }}>
      <div className="dashboard">
        <header className="dashboard-header">
          <div className="logo-container">
            <div className="vd-logo">
              <div className="vd-square">
                <div className="vd-text">V</div>
                <div className="vd-text">D</div>
                <div className="vd-horizontal-line"></div>
                <div className="vd-vertical-line"></div>
              </div>
            </div>
            <div className="logo-text">VDEX</div>
          </div>

          {/* Horizontal Navigation Bar - Now in the middle of the header */}
          <div className="nav-container">
            <HorizontalNavBar activeTab={activeTab} setActiveTab={handleTabClick} />
          </div>

          <div className="user-controls">
            <div className="user-info">
              <span>Welcome, {user.username || user.email}</span>
            </div>
            <button className="logout-button" onClick={onLogout}>Logout</button>
          </div>
        </header>

        {notification.message && (
          <div className={`dashboard-notification ${notification.type}`}>
            {notification.message}
          </div>
        )}

        <div className="dashboard-content">

          <main className="main-content-full">
            {isLoading ? (
              <div className="loading-spinner-container">
                <div className="loading-spinner"></div>
              </div>
            ) : (
              renderTabContent()
            )}
          </main>
        </div>
      </div>
    </NotificationContext.Provider>
  );
};

export default Dashboard;
